/** Console v0.5: the Review inbox (design brief v0.5 s.6, the Inbox mock-up), on session A's routes (#375,
 * contracts s.12). The approval rules are the server's: a different person approves what they prepared or reviewed,
 * only Lodge releases, the service team never approves. This screen offers what `permissions` allows, says why
 * Approve is off, and shows the server's own words for any refusal. "Ask Preekie about this" waits for a backend. */

import { ApiError } from "../api.js";
import type { ScreenContext } from "./app.js";
import { SCREENS, refreshCounts } from "./app.js";
import { delta, dueLabel, money } from "./format.js";
import { INBOX_COPY, INBOX_TABS, approveBlockedText, noteProblem, offerFor, releaseBlockedText, shortcut, tabFromQuery } from "./inbox-logic.js";
import { actOnItem, fetchItem, fetchItems } from "./items-api.js";
import type { InboxTab, ItemAction, ItemCounts, ItemDetail, ItemRow } from "./items-api.js";
import { button, chip, emptyState, figure, h, legalFooter, listRow, loading, notice, openDialog, pageHead, splitPane, table, tabs, uid } from "./ui.js";

function messageOf(err: unknown, fallback: string): string {
  return err instanceof ApiError ? err.message : fallback;
}

function inbox(ctx: ScreenContext): () => void {
  const practice = ctx.me.account?.type !== "company";
  let tab: InboxTab = tabFromQuery(location.search);
  let selected: string | null = new URLSearchParams(location.search).get("item");
  let organisation = "";
  let rows: ItemRow[] | null = null;
  let counts: ItemCounts | null = null;
  let listError: string | null = null;
  let detail: ItemDetail | null = null;
  let detailError: string | null = null;
  let flash: { text: string; ok: boolean } | null = null;
  let busy = false;
  let gone = false;
  let focusDetail = false;

  const remember = () => {
    // A load that finishes after the person has moved on mustn't write over the page they're on (Back then landed on /inbox).
    if (gone) return;
    const q = new URLSearchParams();
    if (tab !== "awaiting_you") q.set("tab", tab);
    if (selected) q.set("item", selected);
    const s = q.toString();
    history.replaceState(null, "", `/inbox${s ? `?${s}` : ""}`);
  };

  async function loadList(keepSelection: boolean, refetchDetail = true): Promise<void> {
    listError = null;
    try {
      const r = await fetchItems(tab, organisation ? { organisation_id: organisation } : {});
      rows = r.items;
      counts = r.counts;
      // A chosen item (e.g. from Home's queue) stays chosen even if it sits under another tab; otherwise the first row.
      if (!keepSelection || !selected) selected = rows[0]?.item_id ?? null;
    } catch (err) {
      listError = messageOf(err, "Preekie couldn't load the review inbox. Try again in a few minutes.");
      rows = [];
    }
    if (refetchDetail) {
      draw();
      await loadDetail();
    } else {
      // After an action the response already holds the item as it is now: keep it (and the focus on its result).
      remember();
      draw();
    }
  }

  async function loadDetail(): Promise<void> {
    detail = null;
    detailError = null;
    remember();
    if (!selected) return draw();
    draw();
    try {
      detail = (await fetchItem(selected)).item;
    } catch (err) {
      // 404: another organisation's or an unknown item -- the same answer for both (no existence leak).
      detailError = messageOf(err, "Preekie couldn't load this piece of work.");
    }
    draw();
  }

  function pick(id: string): void {
    if (busy || id === selected) return;
    selected = id;
    flash = null;
    void loadDetail();
  }

  async function act(action: ItemAction, note?: string): Promise<boolean> {
    if (!detail || busy) return false;
    busy = true;
    draw();
    const done: Record<ItemAction, string> = {
      approve: INBOX_COPY.approved,
      review: INBOX_COPY.reviewed,
      "send-back": INBOX_COPY.sentBack,
      release: INBOX_COPY.released,
    };
    let ok = false;
    try {
      detail = (await actOnItem(detail.item_id, action, note)).item;
      flash = { text: done[action], ok: true };
      ok = true;
    } catch (err) {
      flash = { text: messageOf(err, "That didn't work. Try again in a few minutes."), ok: false };
      // 409 conflict (it changed since it was read) and the other refusals: show the item as it is now.
      if (err instanceof ApiError && (err.status === 409 || err.status === 403)) {
        try {
          detail = (await fetchItem(detail!.item_id)).item;
        } catch {
          /* the list reload below shows what's there */
        }
      }
    }
    busy = false;
    focusDetail = true;
    await loadList(true, false);
    if (ok) void refreshCounts(ctx.me); // the sidebar badge follows the inbox (A)
    return ok;
  }

  function sendBack(): void {
    const area = h("textarea", "pk5-ask-input");
    area.rows = 4;
    area.maxLength = 1000;
    const id = uid("pk5-note");
    area.id = id;
    const label = h("label", "pk5-label", "Note");
    label.htmlFor = id;
    const problem = h("p", "pk5-form-error");
    problem.setAttribute("role", "alert");
    problem.hidden = true;
    openDialog(INBOX_COPY.sendBackTitle, [h("p", "pk5-muted", INBOX_COPY.sendBackHint), h("div", "pk5-field", label, area), problem], [
      { label: "Cancel", variant: "secondary", safe: true, onClick: (close) => close() },
      {
        label: "Send back",
        variant: "primary",
        onClick: async (close, btn) => {
          const bad = noteProblem(area.value);
          if (bad) {
            problem.textContent = bad;
            problem.hidden = false;
            return;
          }
          btn.disabled = true;
          close();
          await act("send-back", area.value.trim());
        },
      },
    ]);
    area.focus();
  }

  function detailPane(): Node {
    if (detailError) return notice(detailError, "alert");
    if (!selected) return emptyState(INBOX_COPY.pick);
    if (!detail) return loading();
    const d = detail;
    const offer = offerFor(d);
    const head = h(
      "div",
      "pk5-detail-head",
      h(
        "div",
        null,
        h("span", "pk5-muted", practice ? `${d.organisation_name} · prepared by ${d.agent}` : `Prepared by ${d.agent}`),
        h("h2", "pk5-detail-title", d.task)
      ),
      chip(d.status_label)
    );
    const parts: Node[] = [head];
    if (flash) {
      const n = notice(flash.text, flash.ok ? "status" : "alert");
      n.tabIndex = -1;
      n.classList.add("pk5-flash");
      parts.push(n);
    }
    if (d.sent_back_note) parts.push(h("div", "pk5-sent-back", h("strong", null, "Sent back: "), d.sent_back_note));
    parts.push(h("p", "pk5-detail-summary", d.summary));
    if (d.figures.length) {
      parts.push(
        table(
          "Figures",
          [{ label: "Label" }, { label: "Field" }, { label: "Amount (AUD)", num: true }, { label: "vs last period", num: true }],
          d.figures.map((f) => [figure(f.code), f.label, figure(money(f.value_cents)), figure(delta(f.delta_cents))])
        )
      );
    }
    const why = h("div", "pk5-detail-col", h("h3", "pk5-subhead", INBOX_COPY.why), ...d.why.map((w) => h("p", "pk5-dash-line", w)));
    const ev = h("div", "pk5-detail-col", h("h3", "pk5-subhead", INBOX_COPY.evidence), ...(d.evidence.length ? d.evidence.map((e) => h("p", "pk5-dash-line", e)) : [h("p", "pk5-muted", "None attached.")]));
    parts.push(h("div", "pk5-detail-cols", why, ev));

    const actions = h("div", "pk5-detail-actions");
    const reasonId = uid("pk5-approve-why");
    if (offer.approve) {
      const approve = button("Approve", "primary", () => void act("approve"));
      approve.setAttribute("aria-label", `Approve ${d.task}`);
      approve.disabled = busy || !offer.approveEnabled;
      if (!offer.approveEnabled) approve.setAttribute("aria-describedby", reasonId);
      actions.append(approve);
    }
    if (offer.review) actions.append(Object.assign(button("Mark as reviewed", "secondary", () => void act("review")), { disabled: busy }));
    if (offer.sendBack) actions.append(Object.assign(button("Send back with a note", "secondary", sendBack), { disabled: busy }));
    const releaseWhyId = uid("pk5-release-why");
    if (offer.release) {
      const rel = button("Release for lodgement", "primary", () => void act("release"));
      rel.disabled = busy || !offer.releaseEnabled;
      if (!offer.releaseEnabled) rel.setAttribute("aria-describedby", releaseWhyId);
      actions.append(rel);
    }
    if (actions.childElementCount) parts.push(actions);
    const reason = offer.approve && !offer.approveEnabled ? approveBlockedText(d.permissions.approve_blocked) : null;
    if (reason) {
      const r = h("p", "pk5-muted", reason);
      r.id = reasonId;
      parts.push(r);
    }
    const releaseWhy = offer.release && !offer.releaseEnabled ? releaseBlockedText(d.permissions.release_blocked) : null;
    if (releaseWhy) {
      const r = h("p", "pk5-muted", releaseWhy);
      r.id = releaseWhyId;
      parts.push(r);
    }
    if (offer.approve) parts.push(h("p", "pk5-shortcuts", INBOX_COPY.shortcuts));
    return h("div", "pk5-detail", ...parts);
  }

  function draw(): void {
    if (gone) return;
    const org = h("select", "pk5-select");
    org.setAttribute("aria-label", "Organisation");
    org.append(new Option(practice ? "All organisations" : "Your organisation", ""));
    for (const e of ctx.me.entities ?? []) org.append(new Option(e.client_name, e.client_slug));
    org.value = organisation;
    org.addEventListener("change", () => {
      organisation = org.value;
      void loadList(false);
    });
    const listNodes: Node[] = listError
      ? [notice(listError, "alert")]
      : rows === null
        ? [loading()]
        : rows.length === 0
          ? [emptyState(INBOX_COPY.emptyTitle, INBOX_COPY.emptyBody)]
          : rows.map((r) =>
              listRow(
                r.task,
                practice ? `${r.organisation_name} · ${r.agent}` : r.agent,
                figure(dueLabel(r.due, new Date(), ctx.timeZone)),
                r.item_id === selected,
                () => pick(r.item_id),
                chip(r.status_label)
              )
            );
    const panelId = uid("pk5-inbox-panel");
    const pane = splitPane("Inbox items", listNodes, detailPane());
    pane.id = panelId;
    pane.setAttribute("role", "tabpanel");
    const c = counts;
    const section = h(
      "section",
      "pk5-inbox",
      tabs(
        "Inbox views",
        INBOX_TABS.map((t) => ({ id: t.id, label: t.label, count: c ? c[t.id] : null })),
        tab,
        (t) => {
          tab = t;
          flash = null;
          rows = null;
          void loadList(false);
        },
        panelId
      ),
      pane
    );
    section.setAttribute("aria-label", "Inbox");
    const focusedRow = document.activeElement?.classList.contains("pk5-row") ? (document.activeElement as HTMLElement).textContent : null;
    ctx.root.replaceChildren(pageHead(INBOX_COPY.title, INBOX_COPY.intro, practice && (ctx.me.entities?.length ?? 0) > 1 ? [org] : []), section, legalFooter());
    if (focusDetail) {
      focusDetail = false;
      (ctx.root.querySelector(".pk5-flash") as HTMLElement | null)?.focus();
    } else if (focusedRow) {
      // Keep focus on the chosen row across the redraw (keyboard users).
      Array.from(ctx.root.querySelectorAll<HTMLElement>(".pk5-row")).find((x) => x.textContent === focusedRow)?.focus();
    }
  }

  const onKey = (ev: KeyboardEvent) => {
    const t = ev.target as HTMLElement | null;
    const inField = !!t && (t.isContentEditable || /^(INPUT|TEXTAREA|SELECT)$/.test(t.tagName) || !!t.closest("dialog"));
    if (ev.ctrlKey || ev.metaKey || ev.altKey) return;
    const what = shortcut(ev.key, inField || !!document.querySelector("dialog[open]"));
    if (!what || !rows?.length) return;
    if (what === "approve") {
      if (detail && offerFor(detail).approveEnabled && !busy) {
        ev.preventDefault();
        void act("approve");
      }
      return;
    }
    const i = rows.findIndex((r) => r.item_id === selected);
    const next = what === "next" ? Math.min(rows.length - 1, i + 1) : Math.max(0, i - 1);
    const target = rows[next];
    if (target) {
      ev.preventDefault();
      pick(target.item_id);
      queueMicrotask(() => ctx.root.querySelectorAll<HTMLElement>(".pk5-row")[next]?.focus());
    }
  };
  document.addEventListener("keydown", onKey);

  draw();
  void loadList(true);
  return () => {
    gone = true;
    document.removeEventListener("keydown", onKey);
  };
}

SCREENS.inbox = inbox;
